import React, { useState, useEffect } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';

interface ProcessingIndicatorProps {
  isProcessing: boolean;
  currentStep: 'idle' | 'uploading' | 'ocr' | 'analyzing' | 'complete' | 'error';
  error?: string | null;
  className?: string;
  onRetry?: () => void;
}

const LOADING_MESSAGES = [
  'Reading your document...',
  'Extracting text with OCR...',
  'Identifying key dates and deadlines...',
  'Detecting notice type and agency...',
  'Analyzing compliance requirements...',
  'Building your action plan...',
  'Almost there...',
];

export const ProcessingIndicator: React.FC<ProcessingIndicatorProps> = ({
  isProcessing,
  currentStep,
  error,
  className = '',
  onRetry,
}) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    if (!isProcessing || error) return;

    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setMessageIndex((prev) =>
          prev < LOADING_MESSAGES.length - 1 ? prev + 1 : prev
        );
        setFade(true);
      }, 400);
    }, 3000);

    return () => clearInterval(interval);
  }, [isProcessing, error]);

  // Reset on new processing run
  useEffect(() => {
    if (isProcessing && !error) {
      setMessageIndex(0);
      setFade(true);
    }
  }, [isProcessing, error]);

  if (!isProcessing && currentStep === 'idle' && !error) {
    return null;
  }

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center py-20 ${className}`}>
        <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
          <svg className="w-6 h-6 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <p className="text-base font-medium text-foreground mb-1">Something went wrong</p>
        <p className="text-sm text-muted-foreground text-center max-w-sm mb-6">{error}</p>
        {onRetry && (
          <Button variant="outline" onClick={onRetry}>
            Try again
          </Button>
        )}
      </div>
    );
  }

  if (currentStep === 'complete') {
    return (
      <div className={`flex flex-col items-center justify-center py-20 ${className}`}>
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-base font-medium text-foreground">Analysis complete</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center justify-center py-20 ${className}`}>
      <Spinner className="size-8 text-primary mb-6" />
      <p
        className={`text-base text-muted-foreground transition-opacity duration-400 ${
          fade ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {LOADING_MESSAGES[messageIndex]}
      </p>
      <div className="flex gap-1.5 mt-6">
        {LOADING_MESSAGES.map((_, i) => (
          <div
            key={i}
            className={`h-1 rounded-full transition-all duration-500 ${
              i <= messageIndex
                ? 'w-4 bg-primary/40'
                : 'w-1.5 bg-border'
            }`}
          />
        ))}
      </div>
    </div>
  );
};
